import React, { useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Image,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Ionicons } from '@expo/vector-icons';
import { PhotoSlotsUpload } from './PhotoSlotsUpload';

const MAX_PHOTOS = 3;
const MAX_SETLIST_SONGS = 5;

export const RoomEqForm = forwardRef(function RoomEqForm({ visible, onClose, venueId, onSaved, embedded = false, saveTitle = null, uploaderDisplayName = null, hideFooter = false }, ref) {
  const [photos, setPhotos] = useState([{ file: null, preview: null, description: '' }]);
  const [setlist, setSetlist] = useState(['']);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const addSetlistRow = () => {
    if (setlist.length >= MAX_SETLIST_SONGS) return;
    setSetlist((prev) => [...prev, '']);
  };

  const removeSetlistRow = (index) => {
    if (setlist.length <= 1) return;
    setSetlist((prev) => prev.filter((_, i) => i !== index));
  };

  const setSetlistItem = (index, value) => {
    setSetlist((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
      const db = getFirestore();
      const storage = getStorage();
      const uid = auth.currentUser.uid;
      const timestamp = Date.now();
      const uploadedPhotos = [];
      for (let i = 0; i < photos.length; i++) {
        const slot = photos[i];
        if (!slot.file) continue;
        const file = slot.file;
        let blob;
        if (file?.uri) {
          blob = await (await fetch(file.uri)).blob();
        } else if (file instanceof Blob) {
          blob = file;
        } else {
          blob = await (await fetch(file)).blob();
        }
        const ext = (file?.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
        const path = `venues/${venueId}/roomEq/${uid}_${timestamp}_${i}.${ext}`;
        const storageRefPath = storageRef(storage, path);
        await uploadBytes(storageRefPath, blob);
        const url = await getDownloadURL(storageRefPath);
        uploadedPhotos.push({ url, description: (slot.description || '').trim() });
      }
      const setlistSongs = setlist.map((s) => (s || '').trim()).filter(Boolean);
      const payload = {
        type: 'eq',
        userId: uid,
        uploaderDisplayName: uploaderDisplayName || auth.currentUser?.displayName || 'Anonymous',
        photos: uploadedPhotos,
        setlist: setlistSongs,
        isPublic,
        createdAt: serverTimestamp(),
      };
      if (saveTitle && String(saveTitle).trim()) payload.title = String(saveTitle).trim();
      await addDoc(collection(db, `venues/${venueId}/roomEq`), payload);
      await updateDoc(doc(db, 'users', uid), { [`venueIdsWithRoomEq.${venueId}`]: true });
      onSaved?.();
      onClose();
      setPhotos([{ file: null, preview: null, description: '' }]);
      setSetlist(['']);
      setIsPublic(true);
    } catch (err) {
      console.error('Room EQ save error:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getPayload = async () => {
    const withFiles = photos.filter((p) => p.file);
    const descriptionOnlyNotes = photos
      .filter((p) => !p.file && (p.description || '').trim().length > 0)
      .map((p) => (p.description || '').trim());
    const setlistSongs = setlist.map((s) => (s || '').trim()).filter(Boolean);
    // If there is no EQ content at all, treat as empty
    if (withFiles.length === 0 && setlistSongs.length === 0 && descriptionOnlyNotes.length === 0) return null;

    setError(null);
    setSaving(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) await signInAnonymously(auth);
      const storage = getStorage();
      const uid = auth.currentUser.uid;
      const timestamp = Date.now();
      const uploadedPhotos = [];
      for (let i = 0; i < photos.length; i++) {
        const slot = photos[i];
        if (!slot.file) continue;
        const file = slot.file;
        let blob;
        if (file?.uri) blob = await (await fetch(file.uri)).blob();
        else if (file instanceof Blob) blob = file;
        else blob = await (await fetch(file)).blob();
        const ext = (file?.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
        const path = `venues/${venueId}/roomEq/${uid}_${timestamp}_${i}.${ext}`;
        const storageRefPath = storageRef(storage, path);
        await uploadBytes(storageRefPath, blob);
        const url = await getDownloadURL(storageRefPath);
        uploadedPhotos.push({ url, description: (slot.description || '').trim() });
      }
      return { photos: uploadedPhotos, setlist: setlistSongs, notes: descriptionOnlyNotes, isPublic };
    } catch (err) {
      console.error('Room EQ getPayload error:', err);
      setError(err.message || 'Upload failed.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Pre-fill form for editing an existing submission.
   * data: { photos?: [{ url, description }], setlist?: string[], notes?: string[] }
   * Photos with url get preview: url (no file); notes become description-only slots.
   */
  const setFormData = useCallback((data) => {
    if (!data) return;
    const eqPhotos = data.photos || [];
    const notes = data.notes || [];
    const slots = eqPhotos.map((p) => ({
      file: null,
      preview: p.url || null,
      description: (p.description || '').trim(),
    }));
    notes.forEach((n) => {
      slots.push({ file: null, preview: null, description: (n || '').trim() });
    });
    if (slots.length === 0) slots.push({ file: null, preview: null, description: '' });
    setPhotos(slots);
    const list = (data.setlist || []).length ? data.setlist : [''];
    setSetlist(list);
  }, []);

  useImperativeHandle(ref, () => ({ getPayload, setFormData }), [photos, setlist, isPublic, venueId, setFormData]);

  const resetAndClose = () => {
    setPhotos([{ file: null, preview: null, description: '' }]);
    setSetlist(['']);
    setIsPublic(true);
    setError(null);
    onClose();
  };

  const formContent = (
    <>
      <Text style={styles.subtitle}>Upload up to 3 photos of your EQ settings and add a description for each.</Text>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            <PhotoSlotsUpload photos={photos} onPhotosChange={setPhotos} maxPhotos={MAX_PHOTOS} />
            <View style={styles.setlistSection}>
              <Text style={styles.setlistTitle}>PA Playlist (optional)</Text>
              <Text style={styles.setlistSubtitle}>Add songs you played at this venue</Text>
              {setlist.map((song, index) => (
                <View key={index} style={styles.setlistRow}>
                  <Text style={styles.setlistNumber}>{index + 1}.</Text>
                  <TextInput
                    style={styles.setlistInput}
                    placeholder="Song name"
                    placeholderTextColor="#000"
                    value={song}
                    onChangeText={(t) => setSetlistItem(index, t)}
                  />
                  <TouchableOpacity
                    onPress={() => removeSetlistRow(index)}
                    disabled={setlist.length <= 1}
                    style={styles.setlistRemoveBtn}
                  >
                    <Ionicons name="close-circle-outline" size={22} color={setlist.length <= 1 ? '#000' : '#000'} />
                  </TouchableOpacity>
                </View>
              ))}
              {setlist.length < MAX_SETLIST_SONGS && (
                <TouchableOpacity onPress={addSetlistRow} style={styles.addSlot}>
                  <Ionicons name="add-circle-outline" size={28} color="#424252" />
                  <Text style={styles.addSlotText}>Add song</Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
      {!hideFooter ? (
      <View style={styles.footer}>
        <View style={styles.footerToggleWrap}>
          <Text style={styles.toggleLabel}>Show publicly on venue page</Text>
          <TouchableOpacity
            style={[styles.toggle, isPublic && styles.toggleOn]}
            onPress={() => setIsPublic((p) => !p)}
          >
            <View style={[styles.toggleThumb, isPublic && styles.toggleThumbOn]} />
          </TouchableOpacity>
        </View>
        <View style={styles.footerButtons}>
          <TouchableOpacity onPress={resetAndClose} style={styles.cancelBtn}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleSave} style={[styles.saveBtn, saving && { opacity: 0.9, flexDirection: 'row', gap: 8 }]} disabled={saving}>
            {saving ? (
              <>
                <ActivityIndicator size="small" color="#fff" />
                <Text style={styles.saveText}>Saving...</Text>
              </>
            ) : (
              <Text style={styles.saveText}>Save</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
      ) : null}
    </>
  );

  if (embedded) {
    if (!visible) return null;
    return <View style={styles.embeddedWrap}>{formContent}</View>;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={resetAndClose}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>Share Room EQ</Text>
            <TouchableOpacity onPress={resetAndClose} style={styles.closeBtn}>
              <Ionicons name="close" size={24} color="#1d1d1d" />
            </TouchableOpacity>
          </View>
          {formContent}
        </View>
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
    embeddedWrap: { flex: 1, minHeight: 200, fontFamily: 'Computer Says No' },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  title: { fontSize: 28, fontWeight: '700', color: '#000', fontFamily: 'Computer Says No' },
  closeBtn: { padding: 4 },
  subtitle: { fontSize: 22, color: '#000', paddingHorizontal: 16, paddingTop: 8, fontFamily: 'Computer Says No' },
  errorText: { fontSize: 20, color: '#000', paddingHorizontal: 16, paddingTop: 8, fontFamily: 'Computer Says No' },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 24 },
  photoCard: { marginBottom: 24 },
  previewWrap: {
    width: '100%',
    aspectRatio: 4 / 3,
    maxHeight: 280,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  previewImage: { width: '100%', height: '100%' },
  placeholder: {
    width: '100%',
    minHeight: 160,
    borderRadius: 12,
    backgroundColor: '#fafafa',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
    ...(Platform.OS === 'web' && { cursor: 'pointer', display: 'block' }),
  },
  placeholderInner: { alignItems: 'center', justifyContent: 'center', padding: 24 },
  placeholderText: { fontSize: 20, color: '#000', marginTop: 8, fontFamily: 'Computer Says No' },
  uploadLink: { fontSize: 20, color: '#000', marginTop: 4, fontFamily: 'Computer Says No' },
  slotActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
    marginBottom: 10,
  },
  changeLabel: {},
  changeLink: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  removeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 0 },
  removeBtnText: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  descInput: {
    borderWidth: 1,
    borderColor: '#000',
    borderRadius: 8,
    padding: 12,
    fontSize: 20,
    color: '#000',
    minHeight: 48,
    textAlignVertical: 'top',
    fontFamily: 'Computer Says No',
  },
  addSlot: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  addSlotText: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  setlistSection: { marginTop: 24, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#000' },
  setlistTitle: { fontSize: 22, fontWeight: '600', color: '#000', marginBottom: 4, fontFamily: 'Computer Says No' },
  setlistSubtitle: { fontSize: 20, color: '#000', marginBottom: 12, fontFamily: 'Computer Says No' },
  setlistRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 },
  setlistNumber: { fontSize: 20, color: '#000', minWidth: 28, fontFamily: 'Computer Says No' },
  setlistInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#000',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontSize: 20,
    color: '#000',
    fontFamily: 'Computer Says No',
  },
  setlistRemoveBtn: { padding: 4 },
  toggleLabel: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  toggle: {
    width: 48,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#ccc',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleOn: { backgroundColor: '#000' },
  toggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#fff',
    alignSelf: 'flex-start',
  },
  toggleThumbOn: { alignSelf: 'flex-end' },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#000',
  },
  footerToggleWrap: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  footerButtons: { flexDirection: 'row', gap: 12 },
  cancelBtn: { paddingVertical: 10, paddingHorizontal: 16 },
  cancelText: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  saveBtn: { paddingVertical: 10, paddingHorizontal: 24, backgroundColor: '#000', borderRadius: 8, minWidth: 80, alignItems: 'center' },
  saveText: { fontSize: 20, color: '#fff', fontWeight: '600', fontFamily: 'Computer Says No' },
});
